import { KnownDeliveryDtoDeliveryType, KnownKioskTransactionType } from '@groundfloor-platform/mgmt-service-g-sdk';
import { SVGIconComponent } from '../../../ui/components/pure/icons/types';
import { convertHyphenated } from '../../../core/util/string-conversion';
import { deliveryTypeToIconMap } from './delivery-type-to-icon.map';
import { kioskTransactionTypeToDeliveryTypeMap } from './kiosk-transaction-type-to-delivery-type.map';

export type TransactionTypeIcon = {
  Icon: SVGIconComponent;
  /** The delivery type the icon stands for, e.g. "Inbound Parcel". */
  label: string;
  /**
   * The transaction types behind the icon, e.g. "Deliver Inbound Parcel". Both directions when both
   * are held, so hovering an icon says what the collapsed icon does not.
   */
  transactionTypeLabels: string[];
};

/**
 * Kiosk transaction types as the delivery-type icons the UI already has. Deliver and collect for the
 * same delivery type share one icon, so a grant on both shows once — the icons say what kind of
 * delivery is handled, not which direction. The direction survives in `transactionTypeLabels`.
 *
 * Shared by lockers and couriers so the same permissions read the same way on both pages.
 */
export function mapTransactionTypesToIcons(transactionTypes: string[]): TransactionTypeIcon[] {
  // Grouped rather than de-duplicated, so each icon keeps the transaction types that produced it.
  const byDeliveryType = new Map<KnownDeliveryDtoDeliveryType, string[]>();

  for (const transactionType of transactionTypes) {
    const deliveryType = kioskTransactionTypeToDeliveryTypeMap[transactionType as KnownKioskTransactionType];
    if (deliveryType === undefined) {
      continue;
    }

    const existing = byDeliveryType.get(deliveryType);
    if (existing) {
      existing.push(transactionType);
    } else {
      byDeliveryType.set(deliveryType, [transactionType]);
    }
  }

  return [...byDeliveryType.entries()]
    .map(([deliveryType, sourceTypes]) => ({
      Icon: deliveryTypeToIconMap[deliveryType],
      label: convertHyphenated(deliveryType, { capitalizeEachWord: true }),
      transactionTypeLabels: sourceTypes.map((type) => convertHyphenated(type, { capitalizeEachWord: true }))
    }))
    .filter((item) => item.Icon !== undefined);
}
